// Serialization + display helpers for a project's saved capture plan.
// The stored plan holds the raw calculator inputs (single source of truth)
// plus a version + savedAt; everything shown is recomputed from those
// inputs via calculateTimelapse, so display never drifts from the math.
import {
  DEFAULT_CALCULATOR_STATE,
  calcParamsFromState,
  calculateTimelapse,
  deriveDisplayStats,
  countDaysPerWeek,
  formatDuration,
  formatDurationInput,
  formatInterval,
  formatRecordingDuration,
  modeToSolveFor,
  solveForToMode,
} from './timelapseCalc';

// v2 added `scenario` + `solveFor` (and the driving inputs). v1 plans are
// still read: planToState fills the two new fields from the stored `mode`.
export const CAPTURE_PLAN_VERSION = 2;

// The calculator-input fields carried in a plan (excludes version/savedAt).
const PLAN_INPUT_KEYS = [
  'scenario',
  'solveFor',
  'mode',
  'durationValue',
  'durationUnit',
  'weekdayMask',
  'dailyStartMin',
  'dailyEndMin',
  'is24h',
  'distanceValue',
  'distanceUnit',
  'speedValue',
  'intervalSeconds',
  'targetVideoSeconds',
  'fps',
  'deadTimeFraction',
  'mbPerFrame',
];

// Hydrate an editable calculator state from a stored plan, falling back to
// defaults for any missing field so a partial/older plan still opens cleanly.
//
// MIGRATION: every plan written before v2 predates both new fields. Such a
// plan is always a long-term project (the only thing v1 could describe), and
// its solve direction lives in `mode` — so the defaults for those two keys are
// derived from the plan rather than taken from DEFAULT_CALCULATOR_STATE, which
// would otherwise force every saved target-mode plan back to solving for
// length.
export function planToState(plan) {
  const s = { ...DEFAULT_CALCULATOR_STATE };
  if (!plan) return s;
  PLAN_INPUT_KEYS.forEach((k) => {
    if (plan[k] !== undefined && plan[k] !== null) s[k] = plan[k];
  });
  if (plan.scenario === undefined || plan.scenario === null) s.scenario = 'project';
  if (plan.solveFor === undefined || plan.solveFor === null) {
    s.solveFor = modeToSolveFor(plan.mode);
  }
  // Whatever resolved above wins; the legacy key follows it so the two can
  // never disagree in memory.
  s.mode = solveForToMode(s.solveFor);
  return s;
}

// Build the persisted plan payload from an editable state. `savedAt` is left
// for the caller to attach (serverTimestamp) so this stays pure/testable.
export function stateToPlan(state) {
  const plan = { version: CAPTURE_PLAN_VERSION };
  PLAN_INPUT_KEYS.forEach((k) => { plan[k] = state[k]; });
  return plan;
}

function weekdaySummary(mask) {
  const days = countDaysPerWeek(mask);
  if (days === 7) return 'every day';
  // Mon–Fri is the common weekday case worth naming explicitly.
  const weekdaysOnly = mask && mask.length === 7
    && mask.slice(0, 5).every(Boolean)
    && !mask[5] && !mask[6];
  if (weekdaysOnly) return 'weekdays';
  return `${days} day${days === 1 ? '' : 's'}/week`;
}

// "2 hours" / "1 day" / "6 months" / "30 mi at 45 mph" — what was recorded, in
// the terms the user entered it. The unit singularises through
// formatDurationInput so a one-day plan does not read "1 days".
export function describeDuration(state) {
  if (state.scenario === 'drive') {
    const speedUnit = state.distanceUnit === 'km' ? 'km/h' : 'mph';
    return `${state.distanceValue} ${state.distanceUnit} at ${state.speedValue} ${speedUnit}`;
  }
  return formatDurationInput(state.durationValue, state.durationUnit);
}

// Compute the display-facing summary for a saved plan: the headline numbers
// the card and coverage strip show. Returns null if the plan can't compute.
export function summarizePlan(plan) {
  if (!plan) return null;
  const state = planToState(plan);
  const result = calculateTimelapse(calcParamsFromState(state));
  if (!result.ok) return null;
  const stats = deriveDisplayStats(result, state);
  const intervalSeconds = state.solveFor === 'interval'
    ? result.recommendedIntervalSeconds
    : state.intervalSeconds;
  // Which finish length the card leads with must be the one the calculator
  // dialog put on screen for this plan, and that depends on the solve
  // direction:
  //   - solving for video LENGTH, the dialog hero is the constant-speed
  //     length ("Your clip runs 4 min 21 s");
  //   - solving for the capture INTERVAL, the hero is the interval and the
  //     sentence quotes the MotionTrim length ("...you land about a 30 s clip");
  //   - solving for recording TIME, the MotionTrim length IS the target the
  //     user typed, so that is the number they came for.
  // The other finish rides underneath as a labelled sub-line, so the gap
  // between the two is explained on the card rather than the card silently
  // printing a different number from the dialog that wrote it.
  const leadsWithTrim = state.solveFor === 'interval' || state.solveFor === 'duration';
  const finalVideoSeconds = leadsWithTrim ? stats.trimmedVideoSeconds : stats.rawVideoSeconds;
  const altVideoSeconds = leadsWithTrim ? stats.rawVideoSeconds : stats.trimmedVideoSeconds;
  // How much still footage the plan allows, as a whole percent. At 0% nothing
  // is trimmed, the two finishes are the same length, and the sub-line would
  // just repeat the headline — so there is no alternative reading to show.
  const stillPercent = Math.round(state.deadTimeFraction * 100);
  return {
    scenario: state.scenario,
    solveFor: state.solveFor,
    mode: state.mode,
    // When the plan solves for recording time, the entered duration is not the
    // answer — the computed one is.
    durationLabel: state.solveFor === 'duration'
      ? formatRecordingDuration(result.requiredRecordingSeconds)
      : describeDuration(state),
    // The weekday mask only describes a long-term project; a single shoot or a
    // drive has no week to speak of, and the card drops the clause.
    weekdayLabel: state.scenario === 'project' ? weekdaySummary(state.weekdayMask) : null,
    intervalLabel: formatInterval(intervalSeconds),
    finalVideoLabel: formatDuration(finalVideoSeconds),
    // The other finish, and its name, so the card can label it without
    // re-deriving the rule above. Null at 0% still footage.
    altVideoLabel: stillPercent > 0 ? formatDuration(altVideoSeconds) : null,
    altVideoName: leadsWithTrim ? 'Constant speed' : 'MotionTrim',
    stillPercent,
    frameCount: stats.frameCount,
    storageGB: stats.storageGB,
    // Rounded planned shooting days — the "plan" half of plan-vs-actual. A
    // single shoot or a drive is one day.
    plannedDays: Math.max(1, Math.round(result.totalShootingDays)),
    durationDays: Math.round(result.durationDays),
  };
}
